import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { API, downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState } from "../components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const RISK_C = { critical: "#DC2626", high: "#EA580C", medium: "#D97706", low: "#059669" };
const ST_C = { online: "#059669", idle: "#059669", completed: "#0891B2", scanning: "#4F46E5", offline: "#94A3B8", paused: "#D97706", error: "#DC2626", disabled: "#64748B", registered: "#0891B2" };
const OS_ICON = { Windows: "Monitor", macOS: "Laptop", Linux: "Server" };

const Chip = ({ color, children, testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded text-[10px] font-semibold uppercase tracking-wide" style={{ background: `${color}15`, color }}>{children}</span>
);

function InstallModal({ open, onClose }) {
  const [os, setOs] = useState("Windows");
  const [token, setToken] = useState(null);
  const server = (process.env.REACT_APP_BACKEND_URL || "https://dpdpcrm.com");
  const genToken = async () => {
    try { const { data } = await api.post("/agents/tokens", { os }); setToken(data.token); toast.success("Registration token generated (valid 72h)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
  const cmds = {
    Windows: [`msiexec /i dpdp-discovery-agent.msi ORG_TOKEN=${token || "<TOKEN>"} SERVER_URL=${server} /qn`,
                `python dpdp_discovery_agent.py register --server-url ${server} --token ${token || "<TOKEN>"}`],
    macOS: [`sudo installer -pkg dpdp-discovery-agent.pkg -target /`,
              `python3 dpdp_discovery_agent.py register --server-url ${server} --token ${token || "<TOKEN>"}`],
    Linux: [`curl -fsSL ${server}/api/agents/installers/install-linux.sh | sudo bash -s -- --server-url ${server} --token ${token || "<TOKEN>"}`,
              `python3 dpdp_discovery_agent.py register --server-url ${server} --token ${token || "<TOKEN>"}`],
  };
  const installers = { Windows: ["msi", "exe"], macOS: ["pkg", "dmg"], Linux: ["deb", "rpm"] };
  const INSTALLER_FILES = { msi: "dpdp-agent-windows.zip", exe: "dpdp-agent-windows.zip",
    pkg: "dpdp-agent-macos.tar.gz", dmg: "dpdp-agent-macos.tar.gz",
    deb: "dpdp-agent.deb", rpm: "dpdp-agent-rhel.tar.gz" };
  const bakeQS = token ? `?token=${encodeURIComponent(token)}&server=${encodeURIComponent(server)}` : "";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" style={{ background: "var(--surface, #fff)" }} data-testid="install-agent-modal">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Icons.HardDriveDownload className="w-4 h-4" style={{ color: "var(--primary)" }} /> Install New Agent</DialogTitle>
          <DialogDescription className="text-xs">Generate a token, install the agent — it auto-registers and starts a full system scan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            {["Windows", "macOS", "Linux"].map(o => {
              const I = Icons[OS_ICON[o]];
              return <button key={o} onClick={() => setOs(o)} data-testid={`os-${o}`} className="flex-1 p-3 rounded-lg text-center" style={{ border: os === o ? "2px solid var(--primary)" : "1px solid var(--border)", background: os === o ? "var(--primary-soft)" : "var(--bg-2)" }}>
                <I className="w-5 h-5 mx-auto mb-1" style={{ color: os === o ? "var(--primary)" : "var(--muted)" }} />
                <div className="text-[12px] font-semibold ink">{o}</div>
              </button>;
            })}
          </div>
          <div className="flex items-center gap-2">
            <Btn onClick={genToken} data-testid="gen-token-btn"><Icons.KeyRound className="w-3.5 h-3.5" /> Generate registration token</Btn>
            {token && <code className="text-[10.5px] mono px-2 py-1 rounded flex-1 truncate" style={{ background: "var(--bg-2)" }} data-testid="agent-token">{token}</code>}
            {token && <Btn variant="secondary" size="sm" onClick={() => copy(token)} data-testid="copy-token">Copy</Btn>}
          </div>
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-1">Step 1 · Download {token && <span className="normal-case font-medium" style={{ color: "#059669" }} data-testid="baked-hint">— token & server URL baked in, zero typing</span>}</div>
            <div className="flex gap-2 flex-wrap">
              {installers[os].map(k => (
                <Btn key={k} variant="secondary" size="sm" onClick={() => downloadFile(`/agents/installers/${k}${bakeQS}`, INSTALLER_FILES[k])} data-testid={`dl-${k}`}>
                  <Icons.Download className="w-3.5 h-3.5" /> {k.toUpperCase()}
                </Btn>
              ))}
              <Btn variant="secondary" size="sm" onClick={() => downloadFile("/agents/installers/agent.py", "dpdp_discovery_agent.py")} data-testid="dl-agent-py">
                <Icons.FileCode2 className="w-3.5 h-3.5" /> Python agent (works now)
              </Btn>
            </div>
          </div>
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-1">Step 2 · Install & register (auto-starts full scan)</div>
            {cmds[os].map((c, i) => (
              <div key={i} className="flex items-center gap-1 mb-1">
                <pre className="flex-1 p-2 rounded text-[10px] mono overflow-x-auto" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>{c}</pre>
                <Btn variant="secondary" size="sm" onClick={() => copy(c)}><Icons.Copy className="w-3 h-3" /></Btn>
              </div>
            ))}
          </div>
          <div className="text-[10.5px] muted p-2 rounded" style={{ background: "var(--bg-2)" }}>
            After install: agent registers → checks in → receives the default <b>Full System DPDP Discovery Scan</b> policy → starts the initial scan automatically → masked findings stream into this dashboard. Raw PII never leaves the endpoint.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FindingRow({ f, onAction, selectable, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg p-3" style={{ border: selected ? "1.5px solid var(--primary)" : "1px solid var(--border)", background: selected ? "var(--primary-soft)" : undefined }} data-testid={`agent-finding-${f.id}`}>
      <div className="flex items-start gap-2">
        {selectable && (
          <input type="checkbox" checked={!!selected} onChange={() => onSelect(f.id)} className="mt-1 accent-indigo-600" data-testid={`select-${f.id}`} />
        )}
        <Icons.FileWarning className="w-4 h-4 mt-0.5 shrink-0" style={{ color: RISK_C[f.risk_level] }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[12.5px] font-semibold ink">{f.file_name}</span>
            <Chip color={RISK_C[f.risk_level]}>{f.risk_level} · {f.risk_score}</Chip>
            <Chip color="#64748B">{f.review_status?.replace(/_/g, " ")}</Chip>
            {f.children_data && <Chip color="#DC2626">children</Chip>}
            {f.credentials && <Chip color="#DC2626">secrets</Chip>}
          </div>
          <div className="text-[10.5px] mono muted truncate mt-0.5">{f.hostname} · {f.file_path}</div>
          <div className="flex gap-1 flex-wrap mt-1">
            {(f.pii_types || []).slice(0, 5).map((p, i) => <Chip key={i} color="var(--primary)">{p}</Chip>)}
            <span className="text-[10px] muted">{f.match_count} matches</span>
          </div>
          <div className="flex gap-1 flex-wrap mt-1">
            {(f.masked_samples || []).slice(0, 3).map((m, i) => <code key={i} className="text-[10px] mono px-1.5 py-[1px] rounded" style={{ background: "var(--bg-2)" }} data-testid={`masked-${f.id}-${i}`}>{m}</code>)}
          </div>
          <button onClick={() => setOpen(!open)} className="text-[10px] font-medium hover:underline mt-1" style={{ color: "var(--primary)" }}>{open ? "Hide" : "Details"}</button>
          {open && (
            <div className="mt-1 p-2 rounded text-[10.5px]" style={{ background: "var(--bg-2)" }}>
              <div><b>Risk reasons:</b> {(f.risk_reasons || []).join("; ")}</div>
              <div className="mt-1"><b>Recommendation:</b> {f.remediation_recommendation}</div>
              <div className="mt-1 muted">Confidence {f.confidence}% · {f.file_type} · dept {f.department || "—"} · modified {(f.last_modified || "").slice(0, 10)}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {f.review_status !== "mapped_to_inventory" && f.review_status !== "false_positive" && (
            <>
              <Btn size="sm" onClick={() => onAction(f, "approve")} data-testid={`approve-${f.id}`}><Icons.CheckCircle2 className="w-3 h-3" /> To inventory</Btn>
              <Btn size="sm" variant="secondary" onClick={() => onAction(f, "false-positive")} data-testid={`fp-${f.id}`}>False positive</Btn>
              <Btn size="sm" variant="secondary" onClick={() => onAction(f, "remediation")} data-testid={`rem-${f.id}`}>Remediation task</Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentDetail({ agent, onClose, onCmd }) {
  const [findings, setFindings] = useState([]);
  useEffect(() => {
    if (agent) api.get(`/agents/${agent.id}/findings`).then(({ data }) => setFindings(data.items || [])).catch(() => {});
  }, [agent]);
  if (!agent) return null;
  const p = agent.scan_progress || {};
  const doAction = async (f, action) => {
    try {
      if (action === "remediation") await api.post(`/agent-findings/${f.id}/remediation`, { action: "Remediate discovered PII" });
      else await api.post(`/agent-findings/${f.id}/${action}`, {});
      toast.success(action === "approve" ? "Mapped to Data Inventory" : action === "remediation" ? "Remediation task created" : "Marked false positive");
      const { data } = await api.get(`/agents/${agent.id}/findings`); setFindings(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!agent} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" style={{ background: "var(--surface, #fff)" }} data-testid="agent-detail">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            {(() => { const I = Icons[OS_ICON[agent.os]] || Icons.Cpu; return <I className="w-4 h-4" style={{ color: "var(--primary)" }} />; })()}
            {agent.agent_name} <Chip color={ST_C[agent.effective_status] || "#64748B"} testId="agent-status-chip">{agent.effective_status}</Chip>
          </DialogTitle>
          <DialogDescription className="text-xs">{agent.hostname} · {agent.os} {agent.os_version} · {agent.ip_address} · v{agent.agent_version} · last check-in {(agent.last_checkin || "").slice(0, 19).replace("T", " ")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          {p.status && (
            <div className="p-3 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid="scan-progress-panel">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="font-semibold ink flex items-center gap-1">
                  {p.status === "running" && <Icons.Loader2 className="w-3 h-3 animate-spin" style={{ color: "var(--primary)" }} />}
                  Scan {p.status} · {p.files_scanned?.toLocaleString?.() || 0} files scanned · {p.files_skipped?.toLocaleString?.() || 0} skipped · {p.findings_count || 0} findings
                </span>
                <span className="tnum font-bold" style={{ color: "var(--primary)" }}>{p.progress_pct || 0}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${p.progress_pct || 0}%`, background: "linear-gradient(90deg,#4F46E5,#7C3AED)" }} />
              </div>
              {p.current_path && <div className="text-[10px] mono muted mt-1 truncate">Scanning: {p.current_path}</div>}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <Btn size="sm" onClick={() => onCmd(agent.id, "scan/start")} data-testid="agent-start-scan"><Icons.Play className="w-3 h-3" /> Full scan</Btn>
            <Btn size="sm" variant="secondary" onClick={() => onCmd(agent.id, "pause")}>Pause</Btn>
            <Btn size="sm" variant="secondary" onClick={() => onCmd(agent.id, "resume")}>Resume</Btn>
            <Btn size="sm" variant="secondary" onClick={() => onCmd(agent.id, "stop")}>Stop</Btn>
            <Btn size="sm" variant="secondary" onClick={() => onCmd(agent.id, "rotate-token")}>Rotate token</Btn>
            <Btn size="sm" variant={agent.rescan_enabled ? "primary" : "secondary"} onClick={() => onCmd(agent.id, "schedule")} data-testid="agent-toggle-rescan">
              <Icons.CalendarClock className="w-3 h-3" /> {agent.rescan_enabled ? "Weekly rescan: ON" : "Enable weekly rescan"}
            </Btn>
            <Btn size="sm" variant="secondary" onClick={() => onCmd(agent.id, "disable")}>Disable</Btn>
            <Btn size="sm" variant="danger" onClick={() => onCmd(agent.id, "delete")} data-testid="agent-unregister">Unregister</Btn>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[["Findings", agent.total_findings], ["Critical", agent.critical_findings], ["Risk score", agent.risk_score], ["Files scanned", (agent.files_scanned_total || 0).toLocaleString()]].map(([l, v]) => (
              <div key={l} className="p-2 rounded-lg text-center" style={{ border: "1px solid var(--border)" }}>
                <div className="text-[9.5px] uppercase tracking-wider muted">{l}</div>
                <div className="text-lg font-bold ink tnum">{v ?? 0}</div>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[11px] font-semibold ink mb-1.5">Findings ({findings.length})</div>
            <div className="space-y-1.5">{findings.map(f => <FindingRow key={f.id} f={f} onAction={doAction} />)}</div>
          </div>
          {(agent.logs || []).length > 0 && (
            <div>
              <div className="text-[11px] font-semibold ink mb-1">Agent logs</div>
              <div className="p-2 rounded text-[10px] mono space-y-0.5 max-h-40 overflow-y-auto" style={{ background: "var(--bg-2)" }} data-testid="agent-logs">
                {agent.logs.map(l => <div key={l.id}><span className="muted">{(l.created_at || "").slice(5, 19)}</span> [{l.level}] {l.message}</div>)}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const HEAT = (n, max) => {
  if (!n) return "var(--bg-2)";
  const t = Math.min(1, n / (max || 1));
  const a = 0.12 + t * 0.6;
  return `rgba(220,38,38,${a.toFixed(2)})`;
};

function HeatmapView({ heatmap, onDrill }) {
  if (!heatmap) return <Loader />;
  if (!heatmap.total_findings) return <EmptyState title="No findings to map" hint="Seed demo agents or run a scan first." />;
  const Grid = ({ title, rows, testId, drillField }) => {
    const maxCrit = Math.max(1, ...rows.map(r => r.critical));
    return (
      <div className="surface rounded-xl p-3" style={{ border: "1px solid var(--border)" }} data-testid={testId}>
        <div className="text-[12.5px] font-semibold ink mb-2">{title} <span className="text-[10px] muted font-normal">· click a row to view findings</span></div>
        <div className="space-y-1">
          <div className="grid grid-cols-[1.6fr_repeat(5,1fr)] gap-1 text-[9.5px] uppercase tracking-wider muted px-1">
            <span></span><span className="text-center">Crit</span><span className="text-center">High</span><span className="text-center">Med</span><span className="text-center">Low</span><span className="text-center">Avg</span>
          </div>
          {rows.map(r => (
            <div key={r.key} onClick={() => onDrill(drillField, r.key)} className="grid grid-cols-[1.6fr_repeat(5,1fr)] gap-1 items-center cursor-pointer rounded hover:bg-slate-50" data-testid={`heat-row-${r.key}`}>
              <span className="text-[11.5px] ink font-medium truncate" title={r.key}>{r.key}</span>
              {["critical", "high", "medium", "low"].map(lv => (
                <span key={lv} className="text-[11px] font-bold tnum text-center py-1 rounded"
                  style={{ background: lv === "critical" ? HEAT(r[lv], maxCrit) : r[lv] ? "var(--bg-2)" : "transparent",
                             color: lv === "critical" && r[lv] ? "#7f1d1d" : "var(--ink)" }}>{r[lv] || ""}</span>
              ))}
              <span className="text-[11px] font-bold tnum text-center" style={{ color: r.avg_risk >= 75 ? "#DC2626" : r.avg_risk >= 50 ? "#EA580C" : "var(--muted)" }}>{r.avg_risk}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-3" data-testid="risk-heatmap">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Grid title="Department risk" rows={heatmap.by_department} testId="heat-department" drillField="department" />
        <Grid title="System risk" rows={heatmap.by_system} testId="heat-system" drillField="hostname" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Grid title="Operating system risk" rows={heatmap.by_os} testId="heat-os" drillField="os" />
        <div className="surface rounded-xl p-3" style={{ border: "1px solid var(--border)" }} data-testid="heat-category">
          <div className="text-[12.5px] font-semibold ink mb-2">PII category distribution</div>
          <div className="space-y-1.5">
            {heatmap.by_category.map(c => {
              const max = Math.max(1, ...heatmap.by_category.map(x => x.count));
              return (
                <div key={c.key} className="flex items-center gap-2">
                  <span className="text-[11px] ink w-28 truncate">{c.key}</span>
                  <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                    <div className="h-full rounded-full" style={{ width: `${c.count / max * 100}%`, background: "var(--primary)" }} />
                  </div>
                  <span className="text-[11px] font-bold tnum muted w-8 text-right">{c.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DiscoveryAgents() {
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({});
  const [findings, setFindings] = useState([]);
  const [tab, setTab] = useState("agents");
  const [heatmap, setHeatmap] = useState(null);
  const [selected, setSelected] = useState([]);
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installOpen, setInstallOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: ag }, { data: fd }] = await Promise.all([api.get("/agents"), api.get("/agent-findings")]);
      setAgents(ag.items || []); setStats(ag.stats || {}); setFindings(fd.items || []);
    } catch { /* nop */ }
    setLoading(false);
  };
  useEffect(() => { load(); return () => pollRef.current && clearInterval(pollRef.current); }, []);
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (agents.some(a => a.effective_status === "scanning")) pollRef.current = setInterval(load, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [agents]);

  const seedDemo = async () => {
    try { const { data } = await api.post("/agents/demo/seed"); toast.success(data.note || `${data.seeded} demo agents installed — initial scans running`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const autoLinkRetention = async () => {
    try { const { data } = await api.post("/agents/retention/auto-link"); toast.success(`${data.created} stale finding(s) scheduled for deletion${data.rule_linked ? " (linked to retention rule)" : ""}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const loadHeatmap = async () => {
    try { const { data } = await api.get("/agents-heatmap"); setHeatmap(data); }
    catch { /* nop */ }
  };
  useEffect(() => { if (tab === "heatmap" && !heatmap) loadHeatmap(); }, [tab]);

  const toggleSelect = (fid) => setSelected(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);
  const bulkAct = async (action) => {
    if (!selected.length) return;
    let owner = null;
    if (action === "assign") { owner = window.prompt("Assign to (owner name/email):"); if (!owner) return; }
    try {
      const { data } = await api.post("/agent-findings/bulk", { ids: selected, action, owner });
      toast.success(`${data.processed} finding(s) processed${data.failed ? `, ${data.failed} failed` : ""}`);
      setSelected([]); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };
  const onDrill = (field, value) => { setDrill({ field, value }); setTab("findings"); };
  const visibleFindings = drill ? findings.filter(f => (f[drill.field] || "Unassigned") === drill.value) : findings;
  const cmd = async (aid, action) => {
    try {
      if (action === "delete") { if (!window.confirm("Unregister this agent?")) return; await api.delete(`/agents/${aid}`); setDetail(null); }
      else if (action === "schedule") { const cur = detail?.rescan_enabled; await api.post(`/agents/${aid}/schedule`, { enabled: !cur, frequency: "weekly" }); }
      else await api.post(`/agents/${aid}/${action}`, {});
      toast.success("Done"); await load();
      if (detail && action !== "delete") { const { data } = await api.get(`/agents/${aid}`); setDetail(data); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openDetail = async (aid) => { try { const { data } = await api.get(`/agents/${aid}`); setDetail(data); } catch { toast.error("Failed"); } };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <PageHeader kicker="Data Governance · Endpoint Discovery" title="Data Discovery Agents"
        description="Install lightweight agents on Windows, macOS and Linux systems. Each agent auto-registers, receives the default full-system DPDP scan policy, and streams masked PII findings, risk scores and scan progress into this dashboard."
        testId="agents-page"
        actions={<>
          <Btn variant="secondary" onClick={seedDemo} data-testid="seed-demo-agents"><Icons.FlaskConical className="w-3.5 h-3.5" /> Seed demo agents</Btn>
          <Btn variant="secondary" onClick={autoLinkRetention} data-testid="auto-link-retention"><Icons.Trash2 className="w-3.5 h-3.5" /> Auto-link retention</Btn>
          <Btn variant="secondary" onClick={() => downloadFile("/agents/reports/discovery.pdf", "endpoint_pii_discovery.pdf")} data-testid="agents-report-pdf"><Icons.FileDown className="w-3.5 h-3.5" /> PDF report</Btn>
          <Btn variant="secondary" onClick={() => downloadFile("/agents/reports/findings.csv", "agent_findings.csv")} data-testid="agents-report-csv"><Icons.Table className="w-3.5 h-3.5" /> CSV</Btn>
          <Btn onClick={() => setInstallOpen(true)} data-testid="install-new-agent"><Icons.Plus className="w-3.5 h-3.5" /> Install new agent</Btn>
        </>} />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
        {[["Agents", stats.total, "Cpu"], ["Online", stats.online, "Wifi"], ["Offline", stats.offline, "WifiOff"], ["Scanning", stats.scanning, "RadarIcon"],
          ["Errors", stats.error, "AlertTriangle"], ["Files scanned", (stats.files_scanned || 0).toLocaleString(), "Files"], ["PII findings", stats.total_findings, "ShieldAlert"], ["Critical", stats.critical_findings, "AlertOctagon"]].map(([l, v, ic]) => {
          const I = Icons[ic] || Icons.Circle;
          return <div key={l} className="surface p-2.5 rounded-xl" style={{ border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-1 text-[9.5px] uppercase tracking-wider muted"><I className="w-3 h-3" />{l}</div>
            <div className="text-xl font-bold ink tnum mt-0.5" data-testid={`stat-${l.toLowerCase().replace(/ /g, "-")}`}>{v ?? 0}</div>
          </div>;
        })}
      </div>

      <div className="flex gap-1 mb-3">
        {[["agents", "Agents"], ["findings", "Discovery Findings"], ["heatmap", "Risk Heatmap"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className="px-3 py-1.5 rounded-md text-[12px] font-medium"
            style={{ background: tab === k ? "var(--primary)" : "var(--bg-2)", color: tab === k ? "white" : "var(--muted)" }}>{l}</button>
        ))}
      </div>

      {loading ? <Loader /> : tab === "heatmap" ? (
        <HeatmapView heatmap={heatmap} onDrill={onDrill} />
      ) : tab === "agents" ? (
        agents.length === 0 ? <EmptyState title="No agents yet" hint="Click 'Install new agent' to generate a token and installer, or 'Seed demo agents' to see the full flow instantly." /> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {agents.map(a => {
              const I = Icons[OS_ICON[a.os]] || Icons.Cpu;
              const p = a.scan_progress || {};
              return (
                <div key={a.id} onClick={() => openDetail(a.id)} className="surface p-3 rounded-xl cursor-pointer card-hover" style={{ border: "1px solid var(--border)" }} data-testid={`agent-card-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--primary-soft)" }}><I className="w-4 h-4" style={{ color: "var(--primary)" }} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold ink truncate">{a.agent_name}</div>
                      <div className="text-[10.5px] muted truncate">{a.hostname} · {a.os} {a.os_version} · {a.department || "—"}</div>
                    </div>
                    <Chip color={ST_C[a.effective_status] || "#64748B"}>{a.effective_status}</Chip>
                  </div>
                  {p.status === "running" && (
                    <div className="mt-2">
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full transition-all duration-700" style={{ width: `${p.progress_pct || 0}%`, background: "var(--primary)" }} />
                      </div>
                      <div className="text-[9.5px] mono muted mt-0.5 truncate">{p.progress_pct}% · {p.current_path}</div>
                    </div>
                  )}
                  <div className="flex gap-3 mt-2 text-[10.5px] muted">
                    <span><b className="ink tnum">{a.total_findings || 0}</b> findings</span>
                    <span style={{ color: "#DC2626" }}><b className="tnum">{a.critical_findings || 0}</b> critical</span>
                    <span>risk <b className="ink tnum">{a.risk_score || 0}</b></span>
                    <span className="ml-auto">{a.last_scan ? `last scan ${(a.last_scan || "").slice(0, 10)}` : "no scan yet"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="space-y-1.5" data-testid="all-findings-list">
          {(drill || visibleFindings.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {drill && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium" style={{ background: "var(--primary-soft)", color: "var(--primary)" }} data-testid="drill-filter-chip">
                  <Icons.Filter className="w-3 h-3" /> {drill.field}: {drill.value}
                  <button onClick={() => setDrill(null)} className="ml-1 font-bold" data-testid="drill-clear">×</button>
                </span>
              )}
              <span className="text-[11px] muted">{visibleFindings.length} finding(s)</span>
              <label className="text-[11px] muted inline-flex items-center gap-1 ml-2">
                <input type="checkbox" className="accent-indigo-600" checked={selected.length > 0 && selected.length === visibleFindings.length}
                  onChange={(e) => setSelected(e.target.checked ? visibleFindings.map(f => f.id) : [])} data-testid="select-all-findings" /> Select all
              </label>
              {selected.length > 0 && (
                <div className="flex items-center gap-1.5 ml-auto" data-testid="bulk-bar">
                  <span className="text-[11px] font-semibold ink">{selected.length} selected</span>
                  <Btn size="sm" onClick={() => bulkAct("approve")} data-testid="bulk-approve"><Icons.CheckCircle2 className="w-3 h-3" /> To inventory</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => bulkAct("assign")} data-testid="bulk-assign">Assign</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => bulkAct("remediation")} data-testid="bulk-remediation">Remediation tasks</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => bulkAct("false_positive")} data-testid="bulk-fp">False positive</Btn>
                </div>
              )}
            </div>
          )}
          {visibleFindings.length === 0 ? <EmptyState title="No discovery findings" hint={drill ? "No findings match this filter." : "Seed demo agents or install a real agent to begin discovery."} /> :
            visibleFindings.map(f => <FindingRow key={f.id} f={f} selectable selected={selected.includes(f.id)} onSelect={toggleSelect} onAction={async (ff, action) => {
              try {
                if (action === "remediation") await api.post(`/agent-findings/${ff.id}/remediation`, { action: "Remediate discovered PII" });
                else await api.post(`/agent-findings/${ff.id}/${action}`, {});
                toast.success("Done"); await load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
            }} />)}
        </div>
      )}

      <InstallModal open={installOpen} onClose={() => setInstallOpen(false)} />
      <AgentDetail agent={detail} onClose={() => setDetail(null)} onCmd={cmd} />
    </div>
  );
}
